/**
 * intentResponseLayer.js — Learner Intent Response Layer (V1)
 * ==========================================================
 *
 * Operator doctrine (Mar 1, 2026):
 *
 *   NO learner free-text input may immediately route to a
 *   dashboard or generic page as the first response. Every
 *   learner input MUST first produce a contextual response
 *   surface (acknowledge → simplify → orient → offer next
 *   actions). Navigation becomes a secondary action chosen by
 *   the learner.
 *
 *   Architectural shift:
 *     OLD: input → route → guidance inside page
 *     NEW: input → response engine → understanding → learner
 *          choices → optional navigation
 *
 * Scope (V1):
 *   • This is NOT a full AI tutor.
 *   • This is a response-interception/orchestration layer.
 *   • Pure function, no LLM, no network, no Mongo.
 *   • Lives next to the existing `understandingIntent.js`
 *     classifier and delegates fear/confusion intents to the
 *     calm UnderstandingCard surface (which already implements
 *     the response-first flow for that category).
 *
 * Public API:
 *
 *   detectIntentCategory(query) → one of INTENT_CATEGORIES
 *   buildIntentResponse(query)  → IntentResponse envelope:
 *     {
 *       category:               <string — see INTENT_CATEGORIES>,
 *       raw:                    <string — original input>,
 *       requires_clarification: <bool>,
 *       delegate:               <"understanding_card" | null>,
 *       topic:                  <string | null>,
 *       phrase:                 <string | null>,
 *       acknowledgement:        <string — heard-you sentence>,
 *       summary:                <string — calm simplification>,
 *       choices: [
 *         {
 *           id:    <string>,
 *           label: <string>,
 *           kind:  "navigate" | "ask_clarify" | "open_understanding"
 *                  | "open_inline",
 *           route: <string | null>,   // ONLY consumed AFTER click
 *         },
 *         ...
 *       ],
 *     }
 *
 *   Doctrine guarantees pinned by `test_intent_response_layer_doctrine.py`:
 *     1. The layer NEVER emits a route as the "primary action" —
 *        routes only live inside `choices[*].route` and are
 *        consumed by the surface ONLY after the learner clicks.
 *     2. `unknown` / empty input ALWAYS yields
 *        requires_clarification=true with a clarifying question
 *        as the headline. Never a dashboard fallback.
 *     3. `booking` ALWAYS asks for day/time preference BEFORE the
 *        booking route is offered.
 *     4. `homework`, `payment`, `progress` ALWAYS produce a
 *        summary first; "open the page" is one of several
 *        learner-chosen options, not the only one.
 *     5. `fear_confusion` delegates to the UnderstandingCard
 *        surface — the orchestration layer does not duplicate
 *        its acknowledgement engine.
 */

import { classifyLearnerIntent } from "./understandingIntent";

// ── Canonical intent categories (V1) ─────────────────────────────
//
// Order is intentional: emotional-first (fear_confusion) → task
// intents → unknown. The minimum 8-category set required by the
// doctrine. Adding a new category is an operator-approved batch.
export const INTENT_CATEGORIES = Object.freeze([
  "fear_confusion",
  "booking",
  "homework",
  "payment",
  "progress",
  "lesson_preparation",
  "instructor_connection",
  "unknown",
]);

// ── Task-intent detection (cheap regex, no LLM) ─────────────────
//
// `fear_confusion` is detected separately by delegating to the
// existing `classifyLearnerIntent` understanding classifier so we
// inherit its operator-locked phrase set (and we never duplicate
// it here).
const TASK_PATTERNS = [
  {
    category: "booking",
    match: /\b(book|booking|book me|reserve|lesson slot|i want a lesson|lesson tomorrow|lesson next|schedule a lesson)\b/i,
  },
  {
    category: "homework",
    match: /\b(homework|reading list|what'?s my homework|what do i need to read|practice list)\b/i,
  },
  {
    category: "payment",
    match: /\b(how much do i owe|pay|payment|invoice|refund|money|stripe|bill)\b/i,
  },
  {
    category: "progress",
    match: /\b(how am i doing|how am i going|my progress|passport|am i ready|how close am i)\b/i,
  },
  {
    category: "lesson_preparation",
    match: /\b(prepare for|prep for|next lesson|what should i practice|warm up|before my lesson)\b/i,
  },
  {
    category: "instructor_connection",
    match: /\b(talk to (?:my )?instructor|message my instructor|call my instructor|reach my instructor|contact my instructor)\b/i,
  },
];

export function detectIntentCategory(query) {
  const raw = String(query || "");
  const q = raw.trim();
  if (!q) return "unknown";

  // Emotional intents win first — delegate to the existing
  // understanding classifier so we never drift from its operator
  // -locked phrase set.
  const cls = classifyLearnerIntent(q);
  if (cls.kind === "understanding") return "fear_confusion";

  for (const { category, match } of TASK_PATTERNS) {
    if (match.test(q)) return category;
  }
  return "unknown";
}

// ── Response envelope builders ──────────────────────────────────
//
// Each builder returns a calm response surface — acknowledgement,
// simplification, and learner-chosen next steps. Routes live
// INSIDE choices and are consumed only when the learner clicks.

function _fearConfusionEnvelope(query, cls) {
  // We do not duplicate the UnderstandingCard's acknowledgement
  // engine. We delegate, and the consuming surface (LearnerHome
  // Intent) renders the UnderstandingCard instead of the generic
  // IntentResponseCard for this category.
  return {
    category: "fear_confusion",
    raw: query,
    requires_clarification: false,
    delegate: "understanding_card",
    topic: cls?.topic || null,
    phrase: cls?.phrase || query,
    acknowledgement:
      "I heard you. Before we go anywhere, let's understand what's going on.",
    summary:
      "Opening the calm Understanding surface — no route, no module, no score.",
    choices: [],
  };
}

function _bookingEnvelope(query) {
  return {
    category: "booking",
    raw: query,
    requires_clarification: false,
    delegate: null,
    topic: null,
    phrase: query,
    acknowledgement:
      "Got it — you want to book a lesson.",
    summary:
      "Before we open the booking screen, tell me roughly when works for you and I'll take you to the right slot.",
    choices: [
      {
        id: "booking_when_tomorrow",
        label: "Tomorrow",
        kind: "ask_clarify",
        route: null,
      },
      {
        id: "booking_when_this_week",
        label: "Later this week",
        kind: "ask_clarify",
        route: null,
      },
      {
        id: "booking_when_flexible",
        label: "I'm flexible — show me what's available",
        kind: "navigate",
        route: "/learn/book",
      },
      {
        id: "booking_open_booking_page",
        label: "Open booking page",
        kind: "navigate",
        route: "/learn/book",
      },
    ],
  };
}

function _homeworkEnvelope(query) {
  return {
    category: "homework",
    raw: query,
    requires_clarification: false,
    delegate: null,
    topic: null,
    phrase: query,
    acknowledgement:
      "Here's a quick read on your homework before we open the page.",
    summary:
      "Your homework is the short list of things to revisit before your next lesson. I can give you the summary here, or open the full homework page.",
    choices: [
      {
        id: "homework_show_summary",
        label: "Show me the summary here",
        kind: "open_inline",
        route: null,
      },
      {
        id: "homework_open_page",
        label: "Open my homework page",
        kind: "navigate",
        route: "/learn/homework",
      },
    ],
  };
}

function _paymentEnvelope(query) {
  return {
    category: "payment",
    raw: query,
    requires_clarification: false,
    delegate: null,
    topic: null,
    phrase: query,
    acknowledgement:
      "Let's take a calm look at your payments.",
    summary:
      "I can show you the short summary of what's owed and what's paid here, or open the full payments page when you're ready.",
    choices: [
      {
        id: "payment_show_summary",
        label: "Show me what's owed",
        kind: "open_inline",
        route: null,
      },
      {
        id: "payment_open_page",
        label: "Open payments page",
        kind: "navigate",
        route: "/money",
      },
    ],
  };
}

function _progressEnvelope(query) {
  return {
    category: "progress",
    raw: query,
    requires_clarification: false,
    delegate: null,
    topic: null,
    phrase: query,
    acknowledgement:
      "Good question — let's look at how you're doing.",
    summary:
      "I can give you the short version here, or open the full progress passport with everything you've worked on.",
    choices: [
      {
        id: "progress_show_summary",
        label: "Show me the short version here",
        kind: "open_inline",
        route: null,
      },
      {
        id: "progress_open_page",
        label: "Open my progress passport",
        kind: "navigate",
        route: "/learn/progress",
      },
    ],
  };
}

function _lessonPrepEnvelope(query) {
  return {
    category: "lesson_preparation",
    raw: query,
    requires_clarification: false,
    delegate: null,
    topic: null,
    phrase: query,
    acknowledgement:
      "Smart — preparing before a lesson is half the work.",
    summary:
      "I can suggest one or two things to revisit before your next lesson, or open the lesson preparation area when you'd like the full list.",
    choices: [
      {
        id: "lesson_prep_suggest_one_thing",
        label: "Suggest one thing I should revisit",
        kind: "open_inline",
        route: null,
      },
      {
        id: "lesson_prep_open_homework",
        label: "Open my homework page",
        kind: "navigate",
        route: "/learn/homework",
      },
    ],
  };
}

function _instructorConnectionEnvelope(query) {
  return {
    category: "instructor_connection",
    raw: query,
    requires_clarification: false,
    delegate: null,
    topic: null,
    phrase: query,
    acknowledgement:
      "Wanting to talk to your instructor is the right instinct.",
    summary:
      "I can drop them a calm note from you, or open the instructor message area so you can reach them directly.",
    choices: [
      {
        id: "instructor_quick_note",
        label: "Send a quick note from me",
        kind: "open_inline",
        route: null,
      },
      {
        id: "instructor_open_messages",
        label: "Open my instructor messages",
        kind: "navigate",
        route: "/learn/messages",
      },
    ],
  };
}

function _unknownEnvelope(query) {
  // Doctrine: unknown / empty MUST yield a clarifying question.
  // Never a dashboard fallback.
  const isEmpty = !String(query || "").trim();
  return {
    category: "unknown",
    raw: String(query || ""),
    requires_clarification: true,
    delegate: null,
    topic: null,
    phrase: null,
    acknowledgement: isEmpty
      ? "I'm here when you're ready."
      : "I want to make sure I help you with the right thing.",
    summary: isEmpty
      ? "Tell me what's on your mind — a feeling, a question, a thing you'd like to do — and I'll take it from there."
      : "Could you tell me a little more about what you mean? I don't want to send you somewhere unhelpful.",
    choices: [
      {
        id: "unknown_clarify_feeling",
        label: "I'm worried or stuck on something",
        kind: "ask_clarify",
        route: null,
      },
      {
        id: "unknown_clarify_booking",
        label: "I want to book or change a lesson",
        kind: "ask_clarify",
        route: null,
      },
      {
        id: "unknown_clarify_progress",
        label: "I want to check how I'm doing",
        kind: "ask_clarify",
        route: null,
      },
      {
        id: "unknown_clarify_other",
        label: "Something else",
        kind: "ask_clarify",
        route: null,
      },
    ],
  };
}

// ── Public builder ──────────────────────────────────────────────

export function buildIntentResponse(query) {
  const raw = String(query || "");

  // Empty / whitespace → unknown clarification.
  if (!raw.trim()) return _unknownEnvelope(raw);

  // Fear / confusion always wins first.
  const cls = classifyLearnerIntent(raw);
  if (cls.kind === "understanding") {
    return _fearConfusionEnvelope(raw, cls);
  }

  const category = detectIntentCategory(raw);
  switch (category) {
    case "booking":               return _bookingEnvelope(raw);
    case "homework":              return _homeworkEnvelope(raw);
    case "payment":               return _paymentEnvelope(raw);
    case "progress":              return _progressEnvelope(raw);
    case "lesson_preparation":    return _lessonPrepEnvelope(raw);
    case "instructor_connection": return _instructorConnectionEnvelope(raw);
    default:                      return _unknownEnvelope(raw);
  }
}

// Test surface — frozen so doctrine pins can introspect without
// risk of mutation drift.
export const _INTERNAL = Object.freeze({
  INTENT_CATEGORIES,
  TASK_PATTERNS,
});
